import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tooltip,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Divider,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { motion } from 'framer-motion';
import StudentForm from '../../components/StudentForm';

const StudentManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    byClass: {}
  });

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    blood_group: '',
    religion: '',
    nationality: 'Nepali',
    admission_date: '',
    class: '',
    section: '',
    roll_number: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'Nepal',
    father_name: '',
    father_occupation: '',
    father_phone: '',
    father_email: '',
    mother_name: '',
    mother_occupation: '',
    mother_phone: '',
    mother_email: '',
    guardian_name: '',
    guardian_relation: '',
    guardian_phone: '',
    guardian_email: '',
    guardian_address: '',
    emergency_contact: '',
    medical_info: ''
  });

  const classes = ['Nursery', 'LKG', 'UKG', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
  const sections = ['A', 'B', 'C', 'D'];
  const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
  const genders = ['male', 'female', 'other'];

  useEffect(() => {
    fetchStudents();
    fetchStats();
  }, [page, rowsPerPage, searchTerm, classFilter, statusFilter]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/students', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: searchTerm,
          class: classFilter,
          status: statusFilter
        }
      });
      
      if (response.data.success) {
        setStudents(response.data.data.students || []);
        setTotalCount(response.data.data.total || 0);
      } else {
        // Handle case when backend returns success: false
        setStudents([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      // Only show error message for actual errors, not for empty data
      if (error.response?.status !== 200) {
        enqueueSnackbar('Failed to fetch students', { variant: 'error' });
      }
      setStudents([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/students/stats/overview');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      const url = editingStudent ? `/api/students/${editingStudent.student_id}` : '/api/students';
      const method = editingStudent ? 'put' : 'post';
      
      const response = await axios[method](url, formData);
      
      if (response.data) {
        enqueueSnackbar(
          editingStudent ? 'Student updated successfully' : 'Student created successfully',
          { variant: 'success' }
        );
        setOpenDialog(false);
        resetForm();
        fetchStudents();
        fetchStats();
      }
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || 'Operation failed',
        { variant: 'error' }
      );
    }
  };

  const handleEdit = (student) => {
    setEditingStudent(student);
    setFormData({
      first_name: student.first_name || '',
      last_name: student.last_name || '',
      date_of_birth: student.date_of_birth ? student.date_of_birth.split('T')[0] : '',
      gender: student.gender || '',
      blood_group: student.blood_group || '',
      religion: student.religion || '',
      nationality: student.nationality || 'Nepali',
      admission_date: student.admission_date ? student.admission_date.split('T')[0] : '',
      class: student.class || '',
      section: student.section || '',
      roll_number: student.roll_number || '',
      phone: student.phone || '',
      email: student.contact_email || student.email || '',
      address: student.address || '',
      city: student.city || '',
      state: student.state || '',
      postal_code: student.postal_code || '',
      country: student.country || 'Nepal',
      father_name: student.father_name || '',
      father_occupation: student.father_occupation || '',
      father_phone: student.father_phone || '',
      father_email: student.father_email || '',
      mother_name: student.mother_name || '',
      mother_occupation: student.mother_occupation || '',
      mother_phone: student.mother_phone || '',
      mother_email: student.mother_email || '',
      guardian_name: student.guardian_name || '',
      guardian_relation: student.guardian_relation || '',
      guardian_phone: student.guardian_phone || '',
      guardian_email: student.guardian_email || '',
      guardian_address: student.guardian_address || '',
      emergency_contact: student.emergency_contact || '',
      medical_info: student.medical_info || ''
    });
    setOpenDialog(true);
  };

  const handleDelete = async (studentId) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      try {
        const response = await axios.delete(`/api/students/${studentId}`);
        if (response.data.success) {
          enqueueSnackbar('Student deleted successfully', { variant: 'success' });
          fetchStudents();
          fetchStats();
        }
      } catch (error) {
        enqueueSnackbar('Failed to delete student', { variant: 'error' });
      }
    }
  };

  const handleToggleStatus = async (studentId, currentStatus) => {
    try {
      const response = await axios.put(`/api/students/${studentId}/toggle-status`);
      if (response.data.success) {
        enqueueSnackbar(
          `Student ${currentStatus === 'active' ? 'deactivated' : 'activated'} successfully`,
          { variant: 'success' }
        );
        fetchStudents();
        fetchStats();
      }
    } catch (error) {
      enqueueSnackbar('Failed to update student status', { variant: 'error' });
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      date_of_birth: '',
      gender: '',
      blood_group: '',
      religion: '',
      nationality: 'Nepali',
      admission_date: '',
      class: '',
      section: '',
      roll_number: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'Nepal',
      father_name: '',
      father_occupation: '',
      father_phone: '',
      father_email: '',
      mother_name: '',
      mother_occupation: '',
      mother_phone: '',
      mother_email: '',
      guardian_name: '',
      guardian_relation: '',
      guardian_phone: '',
      guardian_email: '',
      guardian_address: '',
      emergency_contact: '',
      medical_info: ''
    });
    setEditingStudent(null);
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'error';
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Student Management
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.total}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Total Students
                    </Typography>
                  </Box>
                  <SchoolIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.active}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Active Students
                    </Typography>
                  </Box>
                  <PersonIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.inactive}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Inactive Students
                    </Typography>
                  </Box>
                  <SchoolIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {Object.keys(stats.byClass || {}).length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Classes
                    </Typography>
                  </Box>
                  <SchoolIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search students..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Class</InputLabel>
              <Select
                value={classFilter}
                label="Class"
                onChange={(e) => setClassFilter(e.target.value)}
              >
                <MenuItem value="">All Classes</MenuItem>
                {classes.map((cls) => (
                  <MenuItem key={cls} value={cls}>{cls}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchStudents}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  resetForm();
                  setOpenDialog(true);
                }}
              >
                Add Student
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Students Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Student</TableCell>
                <TableCell>Student Code</TableCell>
                <TableCell>Class & Section</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Guardian</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">Loading...</TableCell>
                </TableRow>
              ) : students.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        No Students Found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {searchTerm || classFilter || statusFilter 
                          ? 'No students match your search criteria' 
                          : 'No students have been added yet'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                students.map((student) => (
                  <TableRow key={student.student_id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {getInitials(student.first_name, student.last_name)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                            {student.first_name} {student.last_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {student.gender} • {student.blood_group}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {student.student_code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${student.class}-${student.section}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Typography variant="body2" color="text.secondary">
                        Roll: {student.roll_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{student.phone}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {student.contact_email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{student.guardian_name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {student.guardian_phone}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={student.status === 'active' ? 'Active' : 'Inactive'}
                        color={getStatusColor(student.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Edit Student">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(student)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={student.status === 'active' ? 'Deactivate' : 'Activate'}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(student.student_id, student.status)}
                            color={student.status === 'active' ? 'warning' : 'success'}
                          >
                            {student.status === 'active' ? <ToggleOffIcon /> : <ToggleOnIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Student">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(student.student_id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 'bold' }}>
          {editingStudent ? 'Edit Student' : 'Add New Student'}
        </DialogTitle>
        <StudentForm formData={formData} setFormData={setFormData} />
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setOpenDialog(false)} size="large">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            size="large"
            sx={{ minWidth: 120 }}
          >
            {editingStudent ? 'Update' : 'Create'} Student
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentManagement;
